import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Upload, FileText, X } from 'lucide-react'
import { DEFAULT_COURSE_TYPES, SUBJECTS } from '@/data/admin-mock-data'
import { getBatches } from '@/data/mentor-mock-data'

const initialMaterials = [
    { id: 'mat1', title: 'Thermodynamics Formula Sheet', subject: 'Physics', courseTypes: ['Crash', 'Long Term'], batches: ['NEET 2025 Batch A'], fileName: 'thermo.pdf', size: '2.4 MB', date: '2024-10-25' },
    { id: 'mat2', title: 'Organic Named Reactions', subject: 'Chemistry', courseTypes: ['Long Term'], batches: ['NEET 2025 Batch A', 'NEET 2025 Batch B'], fileName: 'reactions.pdf', size: '1.8 MB', date: '2024-10-24' },
]

export function AdminMaterialsView() {
    const [materials, setMaterials] = useState(initialMaterials)
    const [modalOpen, setModalOpen] = useState(false)
    const [form, setForm] = useState({ title: '', subject: 'Physics', courseTypes: [] as string[], batches: [] as string[], file: null as File | null })

    const batches = getBatches()

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) setForm(f => ({ ...f, file: e.target.files![0] }))
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.title || !form.file) return
        const file = form.file
        if (!file) return
        setMaterials(prev => [...prev, {
            id: `mat-${Date.now()}`,
            title: form.title,
            subject: form.subject,
            courseTypes: form.courseTypes,
            batches: form.batches,
            fileName: file.name,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
            date: new Date().toISOString().split('T')[0],
        }])
        setForm({ title: '', subject: 'Physics', courseTypes: [], batches: [], file: null })
        setModalOpen(false)
    }

    const toggleCourseType = (c: string) => {
        setForm(f => ({ ...f, courseTypes: f.courseTypes.includes(c) ? f.courseTypes.filter(x => x !== c) : [...f.courseTypes, c] }))
    }

    const toggleBatch = (b: string) => {
        setForm(f => ({ ...f, batches: f.batches.includes(b) ? f.batches.filter(x => x !== b) : [...f.batches, b] }))
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Study Material Upload</h2>
                    <p className="text-sm text-theme-secondary">Upload study materials and assign to subjects, course types, and batches</p>
                </div>
                <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors">
                    <Upload className="h-5 w-5" />
                    Upload
                </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {materials.map((m) => (
                    <GlassmorphicCard key={m.id} className="p-5">
                        <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-xl bg-primary-blue/20 flex items-center justify-center shrink-0">
                                <FileText className="h-6 w-6 text-primary-blue" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-theme-primary truncate">{m.title}</p>
                                <p className="text-sm text-theme-secondary">{m.subject}</p>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {m.courseTypes.map(ct => (
                                        <span key={ct} className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs rounded border border-blue-100 dark:border-blue-900/30">{ct}</span>
                                    ))}
                                    {m.batches.map(b => (
                                        <span key={b} className="px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 text-xs rounded border border-purple-100 dark:border-purple-900/30">{b}</span>
                                    ))}
                                </div>
                                <p className="text-xs text-theme-muted mt-2">{m.fileName} · {m.size} · {m.date}</p>
                            </div>
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">Upload Study Material</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Title</label>
                                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="Material title" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Subject</label>
                                <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary">
                                    {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>

                            {/* Course Types */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">Assign to Course Types</label>
                                <div className="flex flex-wrap gap-2">
                                    {DEFAULT_COURSE_TYPES.map(c => (
                                        <label key={c} className={`flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${form.courseTypes.includes(c) ? 'bg-blue-50 dark:bg-blue-900/20 border-primary-blue text-primary-blue' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary'}`}>
                                            <input type="checkbox" checked={form.courseTypes.includes(c)} onChange={() => toggleCourseType(c)} className="hidden" />
                                            <span className="text-sm font-medium">{c}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Batches */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">Assign to Batches</label>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto custom-scrollbar p-1">
                                    {batches.map(b => (
                                        <label key={b} className={`flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${form.batches.includes(b) ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-600' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary'}`}>
                                            <input type="checkbox" checked={form.batches.includes(b)} onChange={() => toggleBatch(b)} className="hidden" />
                                            <span className="text-sm font-medium">{b}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">File</label>
                                <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-primary-blue file:text-white" required />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold">Upload</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
